import React from "react";
import { useNavigate } from "react-router-dom";

/* ── Logo ──────────────────────────────────────────────────── */
const Logo = ({ size = 38 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 140 140" fill="none">
    <defs>
      <linearGradient id="inv-ng" x1="30" y1="15" x2="110" y2="125">
        <stop offset="0%" stopColor="#00FFE0" /><stop offset="50%" stopColor="#00E5CC" /><stop offset="100%" stopColor="#22D97F" />
      </linearGradient>
    </defs>
    <rect width="140" height="140" rx="30" fill="#0F2140" />
    <path d="M70 18L28 37v32c0 26 42 45 42 45s42-19 42-45V37L70 18z" fill="none" stroke="url(#inv-ng)" strokeWidth="4" />
    <circle cx="70" cy="60" r="16" fill="none" stroke="url(#inv-ng)" strokeWidth="2.5" opacity="0.35" />
    <text x="70" y="68" textAnchor="middle" fontFamily="Sora,sans-serif" fontWeight="800" fontSize="20" fill="url(#inv-ng)">J</text>
  </svg>
);

const Ico = {
  arrowLeft: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  arrowRight: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14" /><path d="M12 5l7 7-7 7" /></svg>,
  globe: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M2 12h20" /></svg>,
  users: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /></svg>,
  barChart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20V10" /><path d="M18 20V4" /><path d="M6 20v-4" /></svg>,
  trending: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M23 6l-9.5 9.5-5-5L1 18" /><path d="M17 6h6v6" /></svg>,
  dollar: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1v22" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>,
  target: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></svg>,
  briefcase: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12l5 5L19 7" /></svg>,
  mail: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" /><path d="M22 6l-10 7L2 6" /></svg>,
  download: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><path d="M7 10l5 5 5-5" /><path d="M12 15V3" /></svg>,
};

export default function InvestorPage() {
  const navigate = useNavigate();

  const highlights = [
    { icon: Ico.globe, value: "45+", label: "Countries Served" },
    { icon: Ico.users, value: "5,000+", label: "Brands Protected" },
    { icon: Ico.barChart, value: "50M+", label: "Tags Deployed" },
    { icon: Ico.trending, value: "99.8%", label: "Accuracy Rate" },
  ];

  const market = [
    { title: "Anti-Counterfeit Market", value: "$4.2B", growth: "Growing 12.3% CAGR", desc: "The global anti-counterfeiting market is expanding rapidly as brands seek digital-first authentication solutions." },
    { title: "NFC Market", value: "$34.9B", growth: "Expected by 2028", desc: "NFC technology adoption in product authentication is accelerating across luxury, pharma, and consumer goods." },
    { title: "Supply Chain Traceability", value: "$6.1B", growth: "Growing 14.1% CAGR", desc: "Regulatory requirements and consumer demand are driving investment in end-to-end traceability solutions." },
  ];

  const whyInvest = [
    "Multi-technology platform (NFC, RFID, QR) — not locked to one approach",
    "SaaS recurring revenue model with strong unit economics",
    "No app required — frictionless consumer experience drives adoption",
    "Built-in consumer engagement (lottery, rewards) increases brand ROI",
    "Global presence: USA headquarters + Asia Pacific operations",
    "Scalable architecture serving small brands to enterprise clients",
    "Strong IP portfolio in authentication and traceability technology",
    "Proven product-market fit with 5,000+ active brands",
  ];

  const timeline = [
    { year: "2023", title: "Founded", desc: "JepTags launched with NFC-based authentication for luxury goods" },
    { year: "2024", title: "Platform Expansion", desc: "Added RFID, QR code support, analytics dashboard, and lottery engagement system" },
    { year: "2024", title: "Global Growth", desc: "Expanded to 45+ countries, onboarded 5,000+ brands, deployed 50M+ tags" },
    { year: "2025", title: "Enterprise & AI", desc: "Launching AI-powered counterfeit detection, enterprise API integrations, and blockchain verification" },
  ];

  return (
    <div className="inv-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* NAV */}
      <nav className="inv-nav">
        <a href="#" className="inv-nav-logo" onClick={e => { e.preventDefault(); navigate('/'); }}>
          <Logo size={36} /><span className="inv-wm"><span className="jep">Jep</span><span className="tags">tags</span></span>
          <span className="inv-slash">/ Investor Relations</span>
        </a>
        <button className="inv-back" onClick={() => navigate(-1)}>
          <span className="inv-ico-16">{Ico.arrowLeft}</span> Back
        </button>
      </nav>

      {/* HERO */}
      <section className="inv-hero">
        <div className="inv-hero-grid">
          <div className="inv-hero-text">
            <div className="inv-badge">Investor Relations</div>
            <h1>Invest in the Future of <span className="grad">Product Authenticity</span></h1>
            <p>JepTags is building the global infrastructure for product authentication and traceability. Join us in fighting the $4.2 trillion counterfeit goods industry.</p>
            <div className="inv-hero-btns">
              <a href="mailto:investors@jeptags.com" className="btn-p">
                <span className="inv-ico-16">{Ico.mail}</span> Contact Investor Relations
              </a>
              <button className="btn-o"><span className="inv-ico-16">{Ico.download}</span> Download Pitch Deck</button>
            </div>
          </div>
          <div className="inv-metrics">
            {highlights.map((h, i) => (
              <div key={i} className="inv-metric">
                <span className="inv-metric-ico">{h.icon}</span>
                <div className="inv-metric-val">{h.value}</div>
                <div className="inv-metric-label">{h.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MARKET */}
      <section className="inv-section">
        <div className="sec-hdr"><h2>Market <span className="grad">Opportunity</span></h2><p>The anti-counterfeit and product traceability markets are experiencing explosive growth.</p></div>
        <div className="inv-market-grid">
          {market.map((m, i) => (
            <div key={i} className="inv-market-card">
              <div className="inv-market-val">{m.value}</div>
              <div className="inv-market-growth">{m.growth}</div>
              <h3>{m.title}</h3>
              <p>{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* WHY INVEST */}
      <section className="inv-section">
        <div className="inv-why-grid">
          <div className="inv-why-text">
            <h2>Why Invest in <span className="grad">JepTags?</span></h2>
            <p className="inv-why-sub">We're uniquely positioned at the intersection of physical products and digital trust — a market every brand in the world needs.</p>
            <div className="inv-why-list">
              {whyInvest.map((point, i) => (
                <div key={i} className="inv-why-item">
                  <span className="inv-check-ico">{Ico.check}</span>
                  <span>{point}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Business Model */}
          <div className="inv-model-card">
            <span className="inv-model-ico">{Ico.briefcase}</span>
            <h3>Business Model</h3>
            <div className="inv-model-items">
              <div className="inv-model-item">
                <span className="inv-mi-ico">{Ico.dollar}</span>
                <div><strong>SaaS Subscriptions</strong><p>Monthly recurring revenue from Starter, Professional, and Enterprise plans</p></div>
              </div>
              <div className="inv-model-item">
                <span className="inv-mi-ico">{Ico.target}</span>
                <div><strong>Tag Revenue</strong><p>Per-tag pricing for NFC, RFID, and QR code hardware</p></div>
              </div>
              <div className="inv-model-item">
                <span className="inv-mi-ico">{Ico.barChart}</span>
                <div><strong>Enterprise Services</strong><p>Custom integrations, API access, and dedicated support</p></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="inv-section">
        <div className="sec-hdr"><h2>Our <span className="grad">Journey</span></h2></div>
        <div className="inv-timeline">
          {timeline.map((t, i, arr) => (
            <div key={i} className="tl-item">
              <div className="tl-left">
                <div className="tl-dot">{t.year}</div>
                {i < arr.length - 1 && <div className="tl-line" />}
              </div>
              <div className="tl-content">
                <h3>{t.title}</h3>
                <p>{t.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="inv-cta-section">
        <div className="inv-cta-box">
          <Logo size={48} />
          <h2>Interested in Partnering with JepTags?</h2>
          <p>We're actively seeking strategic partners and investors who share our vision of a counterfeit-free world.</p>
          <div className="inv-cta-btns">
            <a href="mailto:investors@jeptags.com" className="btn-p lg">
              <span className="inv-ico-16">{Ico.mail}</span> investors@jeptags.com
            </a>
            <button className="btn-o lg" onClick={() => navigate('/')}>
              Learn More <span className="inv-ico-16">{Ico.arrowRight}</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const STYLES = `
.inv-root *,.inv-root *::before,.inv-root *::after{margin:0;padding:0;box-sizing:border-box}
.inv-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;overflow-x:hidden;min-height:100vh;
}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.inv-ico-16 svg{width:16px;height:16px}
.sec-hdr{text-align:center;margin-bottom:48px}
.sec-hdr h2{font-size:clamp(28px,4vw,44px);font-weight:800;letter-spacing:-1px;margin-bottom:12px}
.sec-hdr p{font-size:16px;color:rgba(224,240,240,0.45);max-width:520px;margin:0 auto}

/* NAV */
.inv-nav{position:fixed;top:0;left:0;right:0;z-index:100;padding:16px 40px;display:flex;align-items:center;justify-content:space-between;backdrop-filter:blur(20px);background:rgba(3,10,20,0.85);border-bottom:1px solid rgba(0,229,204,0.06)}
.inv-nav-logo{display:flex;align-items:center;gap:12px;text-decoration:none;cursor:pointer}
.inv-wm{font-size:22px;font-weight:700;letter-spacing:-.5px}
.inv-slash{font-size:13px;color:rgba(224,240,240,0.3);margin-left:4px}
.inv-back{display:flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s}
.inv-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* BUTTONS */
.btn-p{padding:14px 28px;border:none;border-radius:12px;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);font-family:'Sora',sans-serif;font-size:15px;font-weight:700;cursor:pointer;transition:transform .3s,box-shadow .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-p:hover{transform:translateY(-2px);box-shadow:0 12px 40px rgba(0,229,204,0.35)}
.btn-p.lg{font-size:16px;padding:16px 36px}
.btn-o{padding:14px 28px;border:1.5px solid rgba(0,229,204,0.25);border-radius:12px;background:rgba(0,229,204,0.04);color:var(--cyan);font-family:'Sora',sans-serif;font-size:15px;font-weight:600;cursor:pointer;transition:all .3s;display:inline-flex;align-items:center;gap:8px;text-decoration:none}
.btn-o:hover{border-color:var(--cyan);background:rgba(0,229,204,0.08)}
.btn-o.lg{font-size:16px;padding:16px 36px}

/* HERO */
.inv-hero{padding:140px 40px 80px;max-width:1100px;margin:0 auto}
.inv-hero-grid{display:grid;grid-template-columns:1.2fr 1fr;gap:48px;align-items:center}
.inv-badge{display:inline-block;padding:8px 20px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:12px;font-weight:600;letter-spacing:1.5px;text-transform:uppercase;color:var(--cyan);margin-bottom:24px}
.inv-hero-text h1{font-size:clamp(32px,4vw,48px);font-weight:800;line-height:1.1;letter-spacing:-2px;margin-bottom:18px}
.inv-hero-text p{font-size:16px;line-height:1.7;color:rgba(224,240,240,0.5);margin-bottom:28px}
.inv-hero-btns{display:flex;gap:14px;flex-wrap:wrap}

.inv-metrics{display:grid;grid-template-columns:1fr 1fr;gap:16px}
.inv-metric{padding:24px;border-radius:18px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);text-align:center;transition:all .4s}
.inv-metric:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px)}
.inv-metric-ico{width:32px;height:32px;color:var(--cyan);display:inline-flex;margin-bottom:10px}
.inv-metric-ico svg{width:32px;height:32px}
.inv-metric-val{font-size:28px;font-weight:800;font-family:'JetBrains Mono',monospace;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.inv-metric-label{font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:rgba(224,240,240,0.35);margin-top:4px}

/* SECTIONS */
.inv-section{padding:80px 40px;max-width:1100px;margin:0 auto}

/* MARKET */
.inv-market-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.inv-market-card{padding:32px;border-radius:20px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);transition:all .4s}
.inv-market-card:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px)}
.inv-market-val{font-size:36px;font-weight:800;font-family:'JetBrains Mono',monospace;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;margin-bottom:2px}
.inv-market-growth{font-size:12px;font-weight:600;color:var(--teal);margin-bottom:14px}
.inv-market-card h3{font-size:18px;font-weight:700;margin-bottom:8px}
.inv-market-card p{font-size:13px;line-height:1.6;color:rgba(224,240,240,0.45)}

/* WHY INVEST */
.inv-why-grid{display:grid;grid-template-columns:1fr 1fr;gap:48px;align-items:start}
.inv-why-text h2{font-size:clamp(28px,4vw,40px);font-weight:800;letter-spacing:-1px;margin-bottom:12px}
.inv-why-sub{font-size:15px;color:rgba(224,240,240,0.5);line-height:1.6;margin-bottom:28px}
.inv-why-list{display:flex;flex-direction:column;gap:14px}
.inv-why-item{display:flex;align-items:flex-start;gap:12px;font-size:14px;color:rgba(224,240,240,0.6);line-height:1.5}
.inv-check-ico{width:20px;height:20px;flex-shrink:0;color:var(--teal);margin-top:1px;display:flex}
.inv-check-ico svg{width:20px;height:20px}

.inv-model-card{padding:36px;border-radius:24px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.08)}
.inv-model-ico{width:44px;height:44px;color:var(--cyan);display:block;margin-bottom:16px}
.inv-model-ico svg{width:44px;height:44px}
.inv-model-card h3{font-size:22px;font-weight:800;margin-bottom:24px}
.inv-model-items{display:flex;flex-direction:column;gap:20px}
.inv-model-item{display:flex;align-items:flex-start;gap:14px}
.inv-mi-ico{width:36px;height:36px;border-radius:10px;background:rgba(0,229,204,0.08);color:var(--cyan);display:flex;align-items:center;justify-content:center;flex-shrink:0}
.inv-mi-ico svg{width:18px;height:18px}
.inv-model-item strong{font-size:14px;font-weight:700;display:block;margin-bottom:4px}
.inv-model-item p{font-size:13px;color:rgba(224,240,240,0.4);line-height:1.5;margin:0}

/* TIMELINE */
.inv-timeline{max-width:600px;margin:0 auto}
.tl-item{display:flex;gap:20px}
.tl-left{display:flex;flex-direction:column;align-items:center}
.tl-dot{width:48px;height:48px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));color:var(--navy-deep);display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:800;font-family:'JetBrains Mono',monospace;flex-shrink:0}
.tl-line{width:2px;flex:1;background:rgba(0,229,204,0.1);margin:8px 0}
.tl-content{padding-bottom:32px}
.tl-content h3{font-size:16px;font-weight:700;margin-bottom:4px}
.tl-content p{font-size:13px;color:rgba(224,240,240,0.4);line-height:1.5}

/* CTA */
.inv-cta-section{padding:40px 40px 100px}
.inv-cta-box{max-width:700px;margin:0 auto;padding:56px 48px;border-radius:28px;background:linear-gradient(135deg,rgba(0,229,204,0.06),rgba(34,217,127,0.04));border:1px solid rgba(0,229,204,0.1);text-align:center}
.inv-cta-box h2{font-size:clamp(24px,3.5vw,36px);font-weight:800;letter-spacing:-1px;margin:16px 0 12px}
.inv-cta-box p{font-size:16px;color:rgba(224,240,240,0.5);margin-bottom:28px}
.inv-cta-btns{display:flex;gap:14px;justify-content:center;flex-wrap:wrap}

/* RESPONSIVE */
@media(max-width:900px){
  .inv-nav{padding:14px 20px}.inv-slash{display:none}
  .inv-hero{padding:120px 24px 60px}
  .inv-hero-grid{grid-template-columns:1fr}
  .inv-market-grid{grid-template-columns:1fr}
  .inv-why-grid{grid-template-columns:1fr}
  .inv-section{padding:60px 24px}
}
@media(max-width:600px){
  .inv-metrics{grid-template-columns:1fr 1fr}
  .inv-cta-box{padding:36px 24px}
}
`;